import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Check, ChevronDown } from 'lucide-react';

const SIZES = {
  sm: 'h-8 px-2 text-xs',
  md: 'h-10 px-3 text-sm',
  // Tall enough for a `renderValue` trigger carrying an image tile plus two lines.
  lg: 'h-14 px-2.5 text-sm',
};

/**
 * The app's standard dropdown: a custom listbox in the ContextMenu design
 * language (portaled panel, outside-press/Escape/scroll dismissal, viewport
 * clamping) replacing native <select> everywhere for consistent styling.
 *
 * Controlled only. `onChange(value, option)` fires on every pick — including
 * re-picking the current value (action-style pickers rely on this).
 *
 * options: Array<{ value, label, icon?, description?, disabled? }>
 *   `icon` is a rendered node (e.g. <Globe className="h-4 w-4" /> or a colored dot).
 * renderValue(option): custom trigger content for the selected option.
 */
export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  size = 'md',
  disabled = false,
  label,
  className,
  renderValue,
  ariaLabel,
  id,
}) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const typeahead = useRef({ buffer: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [pos, setPos] = useState(null);

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;

  const firstEnabled = (from, dir) => {
    for (let step = 0; step < options.length; step += 1) {
      const i = (from + dir * step + options.length) % options.length;
      if (!options[i]?.disabled) return i;
    }
    return -1;
  };

  const openPanel = () => {
    if (disabled || !options.length) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabled(0, 1));
    setOpen(true);
  };

  const close = (refocus = false) => {
    setOpen(false);
    setPos(null);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (option) => {
    if (option.disabled) return;
    onChange?.(option.value, option);
    close(true);
  };

  // Position below the trigger (flip above when it would overflow the bottom),
  // clamped into the viewport — the ContextMenu pattern.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;
    const below = trigger.bottom + 4;
    const top =
      below + panel.height > window.innerHeight - 8 && trigger.top - panel.height - 4 > 8
        ? trigger.top - panel.height - 4
        : below;
    setPos({
      left: Math.max(8, Math.min(trigger.left, window.innerWidth - panel.width - 8)),
      top: Math.max(8, top),
      minWidth: trigger.width,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPress = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
        close();
      }
    };
    const dismiss = (e) => {
      // Ignore scrolls inside the options panel itself.
      if (e?.target && panelRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPress);
    // Capture phase so scrolls inside nested containers dismiss too.
    document.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      document.removeEventListener('pointerdown', onPress);
      document.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open]);

  // Keep the active option visible while navigating with the keyboard.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    panelRef.current
      ?.querySelector(`#${CSS.escape(`${selectId}-opt-${activeIndex}`)}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, selectId]);

  const onTypeahead = (key) => {
    const now = Date.now();
    const state = typeahead.current;
    state.buffer = now - state.at > 500 ? key : state.buffer + key;
    state.at = now;
    const query = state.buffer.toLowerCase();
    const start = activeIndex >= 0 ? activeIndex : 0;
    for (let step = 0; step < options.length; step += 1) {
      const i = (start + step + (query.length === 1 ? 1 : 0)) % options.length;
      const option = options[i];
      if (!option.disabled && String(option.label).toLowerCase().startsWith(query)) {
        if (open) setActiveIndex(i);
        else pick(option);
        return;
      }
    }
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        openPanel();
      } else if (e.key.length === 1 && e.key !== ' ') {
        onTypeahead(e.key);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => firstEnabled(i + 1, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => firstEnabled(i - 1, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(firstEnabled(0, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(firstEnabled(options.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (options[activeIndex]) pick(options[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        close();
        break;
      default:
        if (e.key.length === 1) onTypeahead(e.key);
    }
  };

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      id={selectId}
      disabled={disabled}
      onClick={() => (open ? close() : openPanel())}
      onKeyDown={onKeyDown}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={open ? `${selectId}-listbox` : undefined}
      aria-label={label ? undefined : ariaLabel}
      className={clsx(
        'flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-100',
        'text-left text-slate-800 transition-colors focus:border-brand-600 focus:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-60',
        SIZES[size] ?? SIZES.md,
        className,
      )}
    >
      <span className={clsx('flex min-w-0 flex-1 items-center gap-2', !selected && 'text-slate-400')}>
        {selected ? (
          renderValue ? (
            renderValue(selected)
          ) : (
            <>
              {selected.icon && <span className="shrink-0">{selected.icon}</span>}
              <span className="truncate">{selected.label}</span>
            </>
          )
        ) : (
          <span className="truncate">{placeholder}</span>
        )}
      </span>
      <ChevronDown
        className={clsx('h-4 w-4 shrink-0 text-slate-400 transition-transform', open && 'rotate-180')}
      />
    </button>
  );

  const panel =
    open &&
    createPortal(
          <div
            ref={panelRef}
            id={`${selectId}-listbox`}
            role="listbox"
            aria-labelledby={selectId}
            style={pos ? { left: pos.left, top: pos.top, minWidth: pos.minWidth } : { left: -9999, top: 0 }}
            className="fixed z-50 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-slate-100 p-1.5 shadow-lg animate-scale-in"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {options.map((option, index) => (
              <button
                key={`${option.value}`}
                type="button"
                id={`${selectId}-opt-${index}`}
                role="option"
                aria-selected={option.value === value}
                disabled={option.disabled}
                onPointerEnter={() => !option.disabled && setActiveIndex(index)}
                onClick={() => pick(option)}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-slate-700',
                  'transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  index === activeIndex && 'bg-slate-200',
                )}
              >
                {option.icon && <span className="shrink-0">{option.icon}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate text-xs text-slate-400">
                      {option.description}
                    </span>
                  )}
                </span>
                {option.value === value && <Check className="h-4 w-4 shrink-0 text-brand-600" />}
              </button>
            ))}
          </div>,
          document.body,
        );

  if (!label) {
    return (
      <>
        {trigger}
        {panel}
      </>
    );
  }

  return (
    <div className="space-y-1">
      <label htmlFor={selectId} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      {trigger}
      {panel}
    </div>
  );
}
